import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './../components/ui/button';
import { Input } from './../components/ui/input';
import { Label } from './../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './../components/ui/select';
import { Separator } from './../components/ui/separator';
import {
  Buildings, Receipt, MapPin, Play, Lock, Info, Check,
} from '@phosphor-icons/react';
import SetupTourDialog from '../components/SetupTourDialog';
import AppFooter from '../components/AppFooter';

// Simplified Settings page modelled on Inspiro Tracker's "Workspace Settings":
// Company profile → Billing & GST → Data Region → Replay Setup Tour.
// Deprecated (removed per product direction): Workspace slug, Preferences
// (date format / visibility / invitation policy), Workspace Info debug card.

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Berlin', label: 'Berlin' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

export default function SettingsPage() {
  const { currentOrg, getOrgHeaders, API, refreshUser } = useAuth();

  // Workspace Settings is owner/admin-only — GST, billing address, data
  // region, and workspace timezone are sensitive org configuration. Anyone
  // else (project_manager, contributor, viewer) gets bounced to the personal
  // /account page, which is the right home for things they CAN edit. We
  // compute `redirect` first then return after all hooks have been declared
  // (React rule: hooks must run unconditionally, in the same order every
  // render).
  const role = currentOrg?.role;
  const allowed = role === 'owner' || role === 'admin';
  const shouldRedirect = !!currentOrg && !allowed;
  const [orgName, setOrgName] = useState('');
  const [settings, setSettings] = useState(null);
  const [billingDraft, setBillingDraft] = useState({
    billing_company_name: '', gst_number: '',
    billing_address_line1: '', billing_city: '', billing_state: '', billing_pincode: '', billing_country: 'India',
  });
  const [tourOpen, setTourOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [billingSaved, setBillingSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/organizations/current`, { withCredentials: true, headers: getOrgHeaders() });
        setOrgName(data.name);
        setSettings(data.settings || {});
        setBillingDraft({
          billing_company_name: data.settings?.billing_company_name || data.name || '',
          gst_number: data.settings?.gst_number || '',
          billing_address_line1: data.settings?.billing_address_line1 || '',
          billing_city: data.settings?.billing_city || '',
          billing_state: data.settings?.billing_state || '',
          billing_pincode: data.settings?.billing_pincode || '',
          billing_country: data.settings?.billing_country || 'India',
        });
      } catch (err) { console.error(err); }
    })();
  }, [currentOrg, API, getOrgHeaders]);

  const canEdit = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/organizations/current`, { name: orgName }, { withCredentials: true, headers: getOrgHeaders() });
      if (settings?.timezone !== settings?.timezone_draft && settings?.timezone_draft) {
        await axios.put(`${API}/organizations/current/settings`, { timezone: settings.timezone_draft }, { withCredentials: true, headers: getOrgHeaders() });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      refreshUser();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTimezoneChange = async (value) => {
    setSettings(s => ({ ...s, timezone: value }));
    try {
      await axios.put(`${API}/organizations/current/settings`, { timezone: value }, { withCredentials: true, headers: getOrgHeaders() });
    } catch (err) { console.error(err); }
  };

  const handleSaveBilling = async () => {
    const payload = { ...billingDraft };
    if (payload.gst_number) {
      const gst = payload.gst_number.trim().toUpperCase();
      const gstinPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinPattern.test(gst)) { alert('Invalid GSTIN format. Example: 29ABCDE1234F1Z5'); return; }
      payload.gst_number = gst;
    }
    try {
      const { data } = await axios.put(`${API}/organizations/current/settings`, payload, { withCredentials: true, headers: getOrgHeaders() });
      setSettings(data);
      setBillingSaved(true);
      setTimeout(() => setBillingSaved(false), 2500);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save billing details'); }
  };

  return (
    <div className="flex-1 overflow-auto flex flex-col" data-testid="settings-page">
      {shouldRedirect && <Navigate to="/account" replace />}
      <div className="px-8 py-6 border-b border-zinc-200">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium mb-1">Workspace</p>
        <h1 className="font-['Outfit'] text-2xl font-bold text-zinc-900 tracking-tight" data-testid="workspace-settings-title">
          Workspace Settings{orgName ? ` — ${orgName}` : ''}
        </h1>
        <p className="text-[12px] text-zinc-500 mt-1">Workspace-scoped configuration: company profile, billing details, data region. For your personal profile, head to <a href="/account" className="text-indigo-600 hover:underline font-medium">My Account</a>.</p>
      </div>

      <div className="p-8 max-w-3xl w-full space-y-5">
        {/* Company Profile */}
        <section className="bg-white border border-zinc-200 rounded-xl" data-testid="company-profile-section">
          <div className="flex items-center gap-3 px-6 py-5 border-b border-zinc-100">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <Buildings size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-base font-bold text-zinc-900">Company Profile</h2>
              <p className="text-[12px] text-zinc-500">Basic company information</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Company Name</Label>
                <Input
                  data-testid="settings-org-name"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  disabled={!canEdit}
                  className="mt-1.5 h-10"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Default Timezone</Label>
                <Select value={settings?.timezone || 'UTC'} onValueChange={handleTimezoneChange} disabled={!canEdit}>
                  <SelectTrigger className="mt-1.5 h-10" data-testid="timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {canEdit && (
              <Button
                data-testid="settings-save-org"
                onClick={handleSaveCompany}
                disabled={saving}
                className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              >
                {saved ? <><Check size={14} className="mr-1" /> Saved</> : saving ? 'Saving…' : 'Save Company Profile'}
              </Button>
            )}
          </div>
        </section>

        {/* Billing & GST Details */}
        <section className="bg-white border border-zinc-200 rounded-xl" data-testid="billing-details-section">
          <div className="flex items-center gap-3 px-6 py-5 border-b border-zinc-100">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Receipt size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-base font-bold text-zinc-900">Billing &amp; GST Details</h2>
              <p className="text-[12px] text-zinc-500">Company billing address and GSTIN for invoices</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Legal Company Name (for Invoice)</Label>
                <Input
                  value={billingDraft.billing_company_name}
                  onChange={e => setBillingDraft({ ...billingDraft, billing_company_name: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Registered company name"
                  className="mt-1.5 h-10"
                  data-testid="billing-company-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">GSTIN</Label>
                <Input
                  value={billingDraft.gst_number}
                  onChange={e => setBillingDraft({ ...billingDraft, gst_number: e.target.value.toUpperCase() })}
                  disabled={!canEdit}
                  placeholder="29ABCDE1234F1Z5"
                  maxLength={15}
                  className="mt-1.5 h-10 font-mono uppercase"
                  data-testid="billing-gstin-input"
                />
                <p className="text-[10px] text-zinc-400 mt-1">Optional · 15 characters</p>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Country</Label>
                <Input
                  value={billingDraft.billing_country}
                  onChange={e => setBillingDraft({ ...billingDraft, billing_country: e.target.value })}
                  disabled={!canEdit}
                  className="mt-1.5 h-10"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Address</Label>
                <Input
                  value={billingDraft.billing_address_line1}
                  onChange={e => setBillingDraft({ ...billingDraft, billing_address_line1: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Street address"
                  className="mt-1.5 h-10"
                  data-testid="billing-address-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">City</Label>
                <Input
                  value={billingDraft.billing_city}
                  onChange={e => setBillingDraft({ ...billingDraft, billing_city: e.target.value })}
                  disabled={!canEdit}
                  className="mt-1.5 h-10"
                  data-testid="billing-city-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">State</Label>
                  <Input
                    value={billingDraft.billing_state}
                    onChange={e => setBillingDraft({ ...billingDraft, billing_state: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1.5 h-10"
                    data-testid="billing-state-input"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Pincode</Label>
                  <Input
                    value={billingDraft.billing_pincode}
                    onChange={e => setBillingDraft({ ...billingDraft, billing_pincode: e.target.value })}
                    disabled={!canEdit}
                    maxLength={6}
                    className="mt-1.5 h-10"
                    data-testid="billing-pincode-input"
                  />
                </div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-400">These details appear on your GST invoices.</p>
            {canEdit && (
              <Button
                onClick={handleSaveBilling}
                className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                data-testid="save-billing-button"
              >
                {billingSaved ? <><Check size={14} className="mr-1.5" /> Saved</> : 'Save billing details'}
              </Button>
            )}
          </div>
        </section>

        {/* Data Region */}
        <section className="bg-white border border-zinc-200 rounded-xl" data-testid="data-region-section">
          <div className="flex items-center gap-3 px-6 py-5 border-b border-zinc-100">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-sky-600 flex items-center justify-center">
              <MapPin size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-base font-bold text-zinc-900">Data Region</h2>
              <p className="text-[12px] text-zinc-500">Where your workspace's data is stored</p>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-orange-500 via-amber-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-white text-[13px] font-black tracking-wide">IN</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-[14px] font-bold text-zinc-900">India</h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                    <Check size={10} weight="bold" /> Active
                  </span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5">All your data is securely stored in Indian data centres.</p>
              </div>
              <Lock size={16} className="text-zinc-400 flex-shrink-0" />
            </div>
            <div className="mt-3 flex items-start gap-2">
              <Info size={13} className="text-zinc-400 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-zinc-500">
                Data region cannot be changed on your current plan. Upgrade to{' '}
                <span className="font-semibold text-indigo-600">Premium</span> to choose from multiple regions including US, EU, and APAC.
              </p>
            </div>
          </div>
        </section>

        {/* Replay Setup Tour */}
        <section className="bg-white border border-zinc-200 rounded-xl p-5 flex items-center justify-between" data-testid="replay-tour-section">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Play size={16} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="font-['Outfit'] text-[14px] font-bold text-zinc-900">Replay Setup Tour</h2>
              <p className="text-[11px] text-zinc-500 truncate">Walk through People, Projects, Custom fields, Card layout, Story points, Board, Backlog, Time tracking, Watchers, Docs, Whiteboard and Integrations.</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTourOpen(true)}
            className="h-9 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 flex-shrink-0 ml-3"
            data-testid="replay-tour-btn"
          >
            <Play size={13} weight="fill" className="mr-1.5" /> Replay Tour
          </Button>
        </section>

        <Separator className="opacity-0" />
      </div>

      <SetupTourDialog open={tourOpen} onClose={() => setTourOpen(false)} />
      <AppFooter />
    </div>
  );
}
